import { type FC, memo } from "react";
import type { RequestLedger, ShiftRequest } from "../../../utils/models";
import { ORIGIN_GROUP_BY_KIND, openingsOf } from "../../../utils/requestOrigins";
import { leadingEntry } from "../../../utils/requestOriginsLedger";
import { GlyphButton } from "../../UIToolkit/Clickable/GlyphButton";
import { HoverCardOrSheet, HoverCardOrSheetSurface } from "../../UIToolkit/Popups/HoverCardOrSheet";
import { RequestOriginCard } from "./RequestOriginCard";
import { cardTitle } from "./requestOriginCopy";
import { ORIGIN_KIND_ICON, type RequestOriginViewLevel } from "./requestOriginMeta";
import { useOriginCopyContext } from "./useOriginCopyContext";

type Props = {
	request: ShiftRequest;
	ledger: RequestLedger;
	level: RequestOriginViewLevel;
	/** Where the phone sheet stops: above the gaps view's bottom nav on its screens, the screen's edge (`undefined`) elsewhere. */
	sheetBottomOffset: string | undefined;
};

/**
 * A request's origin at rest: one muted glyph after its title, shaped by the first thing that opened its slots, with a
 * count when several did. Hovering or focusing it opens "Why this shift was posted"; on a phone a tap opens the same
 * card in a sheet, and the tap never reaches the request card around it.
 */
export const RequestOriginGlyph: FC<Props> = memo(({ request, ledger, level, sheetBottomOffset }) => {
	const copy = useOriginCopyContext();
	const leading = leadingEntry(ledger);
	const Icon = ORIGIN_KIND_ICON[leading.kind];
	const causeCount = openingsOf(ledger.entries).length;
	// No group for an entry with no recorded cause; the attribute is then absent.
	const group = ORIGIN_GROUP_BY_KIND[leading.kind];
	return (
		<HoverCardOrSheet
			dataTestId={`request-origin-card-${request.id}-popup`}
			sheetBottomOffset={sheetBottomOffset}
			content={(surface) => (
				<RequestOriginCard request={request} ledger={ledger} level={level} width={surface === HoverCardOrSheetSurface.Card ? "card" : "fill"} />
			)}
		>
			{({ isOpen, onClick, ariaProps }) => (
				<GlyphButton
					{...ariaProps}
					icon={<Icon />}
					ariaLabel={copy.t("request.origin.glyph.aria", { summary: cardTitle(ledger, copy) })}
					badgeCount={causeCount > 1 ? causeCount : undefined}
					active={isOpen}
					onClick={onClick}
					dataTestId={`request-origin-glyph-${request.id}`}
					data-kind={leading.kind}
					{...(group !== null && { "data-group": group })}
				/>
			)}
		</HoverCardOrSheet>
	);
});
